/**
 * Product Masonry Grid
 * Features:
 * - Masonry layout with random sizes
 * - Hover/Click card expansion
 * - AJAX add to cart (simple products only)
 * - Judge.me integration
 * - Toast notifications
 * - Color swatches as links to product page
 */

class ProductMasonryGrid {
  constructor(container) {
    this.container = container;
    this.cards = container.querySelectorAll('.product-masonry-card');
    this.activeCard = null;
    this.init();
  }

  init() {
    this.setupMasonryLayout();
    this.setupCardExpansion();
    this.setupAddToCart();
    this.initJudgeMe();

    // Recalculate on window resize
    window.addEventListener('resize', this.debounce(() => {
      this.setupMasonryLayout();
    }, 250));
  }

  /**
   * Masonry Layout - Random card positioning
   */
  setupMasonryLayout() {
    const grid = this.container.querySelector('[data-masonry-grid]');
    if (!grid) return;

    this.cards.forEach((card, index) => {
      const size = card.dataset.cardSize || 'medium';
      
      // Apply size classes
      card.classList.remove('size-small', 'size-medium', 'size-large');
      card.classList.add(`size-${size}`);
      
      // Add stagger animation delay
      card.style.animationDelay = `${index * 0.1}s`;
    });
  }

  /**
   * Card Expansion - Hover and Click handlers
   */
  setupCardExpansion() {
    this.cards.forEach(card => {
      const collapsed = card.querySelector('.product-card-collapsed');
      const expanded = card.querySelector('.product-card-expanded');
      const closeBtn = card.querySelector('.product-card-close');
      const plusBtn = card.querySelector('.product-corner-plus');

      // Hover to expand (desktop only)
      if (window.innerWidth > 768) {
        card.addEventListener('mouseenter', () => {
          this.expandCard(card);
        });

        card.addEventListener('mouseleave', (e) => {
          // Don't collapse if moving to child elements
          if (!card.contains(e.relatedTarget)) {
            this.collapseCard(card);
          }
        });
      }

      // Click to expand (mobile + desktop)
      if (plusBtn) {
        plusBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.expandCard(card);
        });
      }

      // Click to collapse
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.collapseCard(card);
        });
      }
    });
  }

  expandCard(card) {
    // Collapse any other active cards
    if (this.activeCard && this.activeCard !== card) {
      this.collapseCard(this.activeCard);
    }

    card.classList.add('is-expanded');
    this.activeCard = card;

    // Trigger layout reflow
    this.reflowCards(card);
  }

  collapseCard(card) {
    card.classList.remove('is-expanded');
    if (this.activeCard === card) {
      this.activeCard = null;
    }

    // Restore layout
    this.reflowCards(null);
  }

  /**
   * Reflow cards when one expands
   */
  reflowCards(expandedCard) {
    const grid = this.container.querySelector('[data-masonry-grid]');
    if (!grid) return;

    this.cards.forEach(card => {
      if (card === expandedCard) {
        card.style.zIndex = '100';
      } else {
        card.style.zIndex = '1';
      }
    });
  }

  /**
   * AJAX Add to Cart (for simple products only)
   */
  setupAddToCart() {
    this.cards.forEach(card => {
      // Only handle button elements (not links for variant products)
      const addToCartBtn = card.querySelector('button.product-add-to-cart');

      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', async (e) => {
          e.preventDefault();

          const variantId = addToCartBtn.dataset.variantId;
          const button = e.currentTarget;
          
          // Disable button during request
          button.disabled = true;
          button.classList.add('loading');

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: variantId,
                quantity: 1
              })
            });

            if (!response.ok) {
              throw new Error('Failed to add to cart');
            }

            const data = await response.json();
            
            // Show success toast
            this.showToast('Dodano do koszyka!');
            
            // Update cart count if exists
            this.updateCartCount();
            
            // Optional: Trigger Shopify cart drawer
            if (typeof window.dispatchEvent === 'function') {
              window.dispatchEvent(new CustomEvent('cart:refresh'));
            }

          } catch (error) {
            console.error('Add to cart error:', error);
            this.showToast('Wystąpił błąd. Spróbuj ponownie.', 'error');
          } finally {
            button.disabled = false;
            button.classList.remove('loading');
          }
        });
      }
    });
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'success') {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;

    const textElement = toast.querySelector('.cart-toast-text');
    if (textElement) {
      textElement.textContent = message;
    }

    toast.classList.remove('show', 'error');
    if (type === 'error') {
      toast.classList.add('error');
    }

    // Show toast
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);

    // Hide after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
    }, 3000);
  }

  /**
   * Update cart count badge
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Initialize Judge.me
   */
  initJudgeMe() {
    // Check if Judge.me is loaded
    if (typeof window.jdgm !== 'undefined') {
      window.jdgm.customBadges();
    } else {
      // Wait for Judge.me to load
      const checkJudgeMe = setInterval(() => {
        if (typeof window.jdgm !== 'undefined') {
          window.jdgm.customBadges();
          clearInterval(checkJudgeMe);
        }
      }, 500);

      // Stop checking after 10 seconds
      setTimeout(() => {
        clearInterval(checkJudgeMe);
      }, 10000);
    }
  }

  /**
   * Utility: Debounce function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.product-masonry-section');
  containers.forEach(container => {
    new ProductMasonryGrid(container);
  });
});

// Reinitialize on Shopify section reload (Theme Editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.product-masonry-section');
    if (container) {
      new ProductMasonryGrid(container);
    }
  });
}

// Handle clicks outside expanded cards to collapse
document.addEventListener('click', (e) => {
  const expandedCard = document.querySelector('.product-masonry-card.is-expanded');
  if (expandedCard && !expandedCard.contains(e.target)) {
    expandedCard.classList.remove('is-expanded');
  }
});

// Keyboard accessibility - ESC to close
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const expandedCard = document.querySelector('.product-masonry-card.is-expanded');
    if (expandedCard) {
      expandedCard.classList.remove('is-expanded');
    }
  }
});
